import { useMemo, useState, useEffect, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import { formatLongDateTimeString, pluralize, undent } from '@bymason/utils';
import {
  Box,
  Typography,
  Divider,
  Tooltip,
  FaMapMarkerAlt,
  GridRowsProp,
  ControllerDataGrid as DataGrid,
  ControllerButtonMenu,
  UxInfoSign,
  Link,
  alpha,
  Button,
  FiArrowRight,
  SxProps,
} from '@bymason-private/legacy-pattern-components';
import { useAuth } from '@bymason-private/auth-provider';
import { Route, RouterLink } from '@bymason-private/route-provider';

import { DeviceModel, DeviceStatus, DeviceLocation, useRenameDevice } from '@bymason/controller-api-hooks';
import {
  LegacyDetailsList,
  DetailItem,
  DetailsListEmpty,
  getBatteryTypeFromLevel,
  EditableText,
} from '@bymason-private/controller-shared-widgets';
import { Theme } from '@bymason-private/controller-v1-theme-provider';

const DEVICE_PRESENCE_OFFLINE_TIP = undent`
  Devices maintain an open connection to Controller to allow remote actions.
  Offline status can be caused by:
  - No connectivity (Wi-Fi or cellular)
  - Airplane mode on
  - Device powered off
  - MQTT connection on port 443 being blocked by a firewall
`;

const actionsGridCols = [
  { field: 'id', headerName: 'ID', width: 200 },
  { field: 'targets', headerName: 'Receiving App', flex: 3 },
  { field: 'command', headerName: 'Command', width: 100 },
  { field: 'args', headerName: 'Args', flex: 2 },
  { field: 'requested', headerName: 'Requested', flex: 1 },
  { field: 'expires', headerName: 'Expires', flex: 1 },
];

const gridRowsWrapWithDynamicHeightSx: SxProps<Theme> = {
  // TODO: Follow this issue for any updates regarding dynamic
  // row height (table-like) grid: https://github.com/mui/mui-x/issues/417
  '& .MuiDataGrid-viewport, & .MuiDataGrid-row': {
    maxHeight: 'fit-content !important',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    height: 'fit-content !important',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    position: 'relative',
  },
  '& .MuiDataGrid-cell': {
    maxHeight: 'fit-content !important',
    whiteSpace: 'initial!important',
    padding: 1,
  },
};

const subSectionSx = {
  display: 'flex',
  columnGap: 1,
  '& > *': {
    flex: '1 1 auto',
  },
};

const subSectionTitleSx = {
  flex: '0 0 120px',
  fontWeight: '700',
  py: '9px',
  overflowX: 'hidden',
};

export function DeviceInfoView({
  deviceInfo,
  deviceStatus,
  deviceLocation,
  hideTitleColumn,
  pendingActionsAsTab = false,
  onNavToPendingTab,
  refreshInfo,
  groupViewRoute,
}: DeviceInfoViewProps) {
  const { authToken = '' } = useAuth();

  const [showActions, setShowActions] = useState(false);

  const [renameDevice, renameResult, renameError, renameBusy] = useRenameDevice(authToken);

  const handleShowHideActions = () => {
    setShowActions(!showActions);
  };

  const handleChangeDeviceName = useCallback(
    (newValue: string) => {
      renameDevice({ name: newValue, deviceId: deviceInfo?.id || '' });
    },
    [deviceInfo?.id, renameDevice]
  );

  const statusItems = useMemo<DetailItem[]>(() => {
    const presence = deviceStatus?.presence.online ? 'Online' : 'Offline';
    const presenceType = deviceStatus?.presence.online ? 'online' : 'offline';
    const battery = deviceInfo?.battery.level ? `${deviceInfo?.battery.level}%` : '';

    const items: DetailItem[] = [
      {
        type: presenceType,
        value: presence,
        label: (
          <Box display='flex' columnGap={0.5} component='span'>
            <span>Connection</span>
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{DEVICE_PRESENCE_OFFLINE_TIP}</span>}
              arrow
              placement='top-start'
            >
              <span>
                <UxInfoSign
                  sx={{
                    fontSize: '0.8em',
                    opacity: 0.3,
                    cursor: 'pointer',
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ];
    deviceInfo?.battery.level &&
      items.push({
        type: getBatteryTypeFromLevel(Number(deviceInfo?.battery.level)),
        label: 'Battery life',
        value: battery,
      });

    return items;
  }, [deviceInfo?.battery.level, deviceStatus?.presence.online]);

  const identificationItems = useMemo<DetailItem[]>(() => {
    const id = deviceInfo?.id ?? '';
    const imei = deviceInfo?.imei ?? '';
    const serial = deviceInfo?.serialnumber ?? '';
    const name = deviceInfo?.name ?? '';
    const family = deviceInfo?.family ?? '';
    const lastCheckin = deviceInfo?.lastcheckin ?? '';
    const groupLink = deviceInfo?.group?.name ? (
      <RouterLink
        linkComponent={<Link>{deviceInfo.group.name}</Link>}
        route={groupViewRoute}
        state={{ name: deviceInfo.group.name }}
      />
    ) : (
      <span />
    );

    return [
      { type: 'id', label: 'Device ID', value: id },
      { type: 'imei', label: 'IMEI', value: imei },
      { type: 'serialnumber', label: 'Serial', value: serial },
      {
        type: 'name',
        label: 'Name',
        value: <EditableText value={name} isBusy={renameBusy} onChange={handleChangeDeviceName} />,
      },
      { type: 'devices', label: 'Model', value: family },
      { type: 'groups', label: 'Group', value: groupLink },
      {
        type: 'date',
        label: 'Last check-in',
        value: lastCheckin ? formatLongDateTimeString(lastCheckin) : '',
      },
    ];
  }, [deviceInfo, renameBusy, groupViewRoute, handleChangeDeviceName]);

  const coords = useMemo(() => {
    return deviceLocation
      ? ({
          lat: Number(deviceLocation?.latitude || '0'),
          lng: Number(deviceLocation?.longitude || '0'),
        } as GoogleMapReact.Coords)
      : null;
  }, [deviceLocation]);

  const locationItems = useMemo<DetailItem[]>(() => {
    return [
      {
        type: 'location',
        label: 'Location',
        value: deviceLocation ? `${deviceLocation?.latitude}, ${deviceLocation?.longitude}` : 'Unknown',
      },
      {
        type: 'date',
        label: 'Last updated',
        value: deviceLocation?.time ? formatLongDateTimeString(deviceLocation?.time) : 'Unknown',
      },
    ];
  }, [deviceLocation]);

  const actionsGridRows = useMemo<GridRowsProp>(() => {
    const actions = deviceStatus?.pendingNotifications || [];

    return actions.map((act) => {
      const { id, targets, command, arguments: args, date: requested, expiration } = act;

      return {
        id,
        targets: targets.join(', ') || 'none',
        command,
        args: args.join(', ') || 'none',
        requested: formatLongDateTimeString(requested),
        expires: formatLongDateTimeString(expiration),
      };
    });
  }, [deviceStatus]);

  useEffect(() => {
    if (renameResult && !renameBusy && !renameError) {
      refreshInfo && refreshInfo();
    }
  }, [refreshInfo, renameBusy, renameResult, renameError]);

  return (
    <div>
      <Box paddingTop={2}>
        <Box sx={subSectionSx}>
          <Typography display={hideTitleColumn ? 'none' : undefined} variant='body2' sx={subSectionTitleSx}>
            Status
          </Typography>
          <LegacyDetailsList items={statusItems} />
        </Box>
        <Box sx={subSectionSx}>
          <Typography display={hideTitleColumn ? 'none' : undefined} variant='body2' sx={subSectionTitleSx}>
            Actions
          </Typography>
          {hideTitleColumn ? (
            <LegacyDetailsList
              items={[
                {
                  type: 'pending',
                  label: (
                    <Box display='flex' height='100%' alignItems='center'>
                      Actions
                    </Box>
                  ),
                  value: pendingActionsAsTab ? (
                    <Button
                      onClick={onNavToPendingTab}
                      variant='outlined'
                      color={actionsGridRows.length >= 1 ? 'error' : undefined}
                      endIcon={<FiArrowRight size='1em' />}
                      sx={{ p: 1 }}
                    >
                      {pluralize(actionsGridRows.length)`$count pending ${['action', 'actions']}`}
                    </Button>
                  ) : (
                    <ControllerButtonMenu
                      onClick={handleShowHideActions}
                      buttonText={pluralize(actionsGridRows.length)`$count pending ${['action', 'actions']}`}
                      disabled={actionsGridRows.length === 0}
                      sx={{ py: 1 }}
                    />
                  ),
                },
              ]}
            />
          ) : (
            <DetailsListEmpty
              content={
                pendingActionsAsTab ? (
                  <Button
                    onClick={onNavToPendingTab}
                    variant='outlined'
                    color='error'
                    endIcon={<FiArrowRight size='1em' />}
                    sx={{ p: 1 }}
                  >
                    {pluralize(actionsGridRows.length)`$count pending ${['action', 'actions']}`}
                  </Button>
                ) : (
                  <ControllerButtonMenu
                    onClick={handleShowHideActions}
                    buttonText={pluralize(actionsGridRows.length)`$count pending ${['action', 'actions']}`}
                    disabled={actionsGridRows.length === 0}
                    sx={{ py: 1 }}
                  />
                )
              }
            />
          )}
        </Box>
        {showActions && actionsGridRows.length > 0 && (
          <DataGrid
            sx={{
              marginBottom: 1,
              maxWidth: 1500,
              ...gridRowsWrapWithDynamicHeightSx,
            }}
            autoHeight
            disableVirtualization
            disableColumnResize={false}
            columns={actionsGridCols}
            rows={actionsGridRows}
          />
        )}
        <Box sx={subSectionSx}>
          <Typography display={hideTitleColumn ? 'none' : undefined} variant='body2' sx={subSectionTitleSx}>
            Identification
          </Typography>
          <LegacyDetailsList items={identificationItems} />
        </Box>
      </Box>
      <Box paddingTop={2}>
        <Box marginBottom={2}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={subSectionSx}>
            <Box display={hideTitleColumn ? 'none' : undefined} sx={subSectionTitleSx} />
            <LegacyDetailsList items={locationItems} />
          </Box>

          <Box
            sx={(theme) => ({
              width: '100%',
              maxWidth: 1000,
              minHeight: 300,
              height: 300,
              marginTop: 2,
              bgcolor: alpha(theme.palette.divider, 0.2),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            })}
          >
            {coords ? (
              <GoogleMapReact
                bootstrapURLKeys={{ key: 'AIzaSyC9CcVzKUrBMXTTFx8PDM_d5Q05i9-Bl8Q' }}
                center={coords}
                zoom={15}
              >
                <Marker lat={coords.lat} lng={coords.lng} />
              </GoogleMapReact>
            ) : (
              <Box display='flex' justifyContent='center' alignItems='center'>
                <Typography>Location not available</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
}

// do not spread coords into div and do not remove eslint
// disable. This is a known issue in google maps react.
// https://github.com/google-map-react/google-map-react/issues/583
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Marker = (coords: { lat: number; lng: number }) => (
  <div>
    <FaMapMarkerAlt fontSize={20} color='#2B95D6' style={{ transform: 'translate(-50%, -100%)' }} />
  </div>
);

interface DeviceInfoViewProps {
  deviceInfo?: DeviceModel | void;
  deviceStatus?: DeviceStatus | void;
  deviceLocation?: DeviceLocation | void;
  refreshBusy?: boolean;
  hideTitleColumn?: boolean;
  pendingActionsAsTab?: boolean;
  onNavToPendingTab?: () => void;
  groupViewRoute: Route;
  refreshInfo?: () => void;
}
